import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Navigate, useSearchParams } from 'react-router-dom';
import { PASSWORD_MIN_LENGTH, type InviteLookupResponse } from '@fametrue/shared';
import { useAuth } from '../auth/AuthProvider';
import { AuthError, AuthScreen } from '../components/AuthScreen';
import { apiRequest } from '../lib/api';
import { useErrorMessage } from '../lib/useErrorMessage';

/**
 * Attivazione dell'invito. Il link arriva all'utente per un canale scelto
 * dall'amministratore: la piattaforma non invia e-mail.
 *
 * Schermata non presente nell'Allegato A, costruita sul guscio della sua login.
 *
 * L'email dell'account si legge dal token e si mostra in un campo in sola
 * lettura con `autocomplete="username"`. Serve a due cose: chi riceve il link
 * sa per quale indirizzo sta scegliendo la password, e il gestore password ha
 * un nome utente a cui legare la credenziale. Senza quel campo, su macOS
 * finisce per salvare come nome utente il link d'invito, e sulla pagina di
 * login la password non viene più proposta.
 */
export function AcceptInvitePage(): ReactNode {
  const { t } = useTranslation();
  const { user, acceptInvite } = useAuth();
  const [searchParams] = useSearchParams();
  const describeError = useErrorMessage();

  const token = searchParams.get('token');

  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: () =>
      apiRequest<InviteLookupResponse>(`/api/auth/invite?token=${encodeURIComponent(token ?? '')}`),
    enabled: token !== null,
    // Un link scaduto o già speso non diventa valido riprovando.
    retry: false,
  });

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/" replace />;

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setLocalError(null);

    if (!token) {
      setLocalError(t('invite.missingToken'));
      return;
    }
    if (password !== confirmation) {
      setLocalError(t('invite.mismatch'));
      return;
    }

    setSubmitting(true);
    try {
      await acceptInvite(token, password);
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  // Un link morto non presenta un modulo da compilare: si dice perché e basta.
  const lookupFailed = token === null || invite.isError;
  const message = localError ?? describeError(error ?? invite.error);

  if (lookupFailed) {
    return (
      <AuthScreen subtitle={t('invite.subtitle')}>
        <AuthError message={message ?? t('invite.missingToken')} />
      </AuthScreen>
    );
  }

  return (
    <AuthScreen subtitle={t('invite.subtitle')}>
      <form onSubmit={(e) => void onSubmit(e)}>
        <p className="login-lead">{t('invite.account')}</p>

        <div className="login-group">
          <label className="login-label" htmlFor="invite-email">
            {t('invite.email')}
          </label>
          <input
            id="invite-email"
            type="email"
            className="login-field"
            autoComplete="username"
            readOnly
            value={invite.data?.email ?? ''}
            // Nessun tabIndex negativo: il campo si deve poter leggere con la
            // tastiera, è l'unico punto in cui l'utente vede il proprio account.
          />
        </div>

        <div className="login-group">
          <label className="login-label" htmlFor="invite-password">
            {t('invite.password')}
          </label>
          <input
            id="invite-password"
            type="password"
            className="login-field"
            autoComplete="new-password"
            autoFocus
            required
            minLength={PASSWORD_MIN_LENGTH}
            disabled={submitting || invite.isPending}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        <div className="login-group">
          <label className="login-label" htmlFor="invite-password-confirm">
            {t('invite.passwordConfirm')}
          </label>
          <input
            id="invite-password-confirm"
            type="password"
            className="login-field"
            autoComplete="new-password"
            required
            disabled={submitting || invite.isPending}
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />
        </div>

        <p className="login-hint">{t('invite.hint')}</p>

        {message ? <AuthError message={message} /> : null}

        <button type="submit" className="login-btn" disabled={submitting || invite.isPending}>
          {submitting ? t('invite.submitting') : t('invite.submit')}
        </button>
      </form>
    </AuthScreen>
  );
}
